import { FormControl, InputLabel, MenuItem, Pagination, Select, SelectChangeEvent } from '@mui/material';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '../../components/Button/Button.tsx';
import { Footer } from '../../components/Footer/Footer.tsx';
import { Header } from '../../components/Header/Header.tsx';
import { Loading } from '../../components/Loading/Loading.tsx';
import { ExamVulnerabilityView } from '../../graphql/types.gen.ts';
import { GENERAL_GROUP_NAME } from '../../index.tsx';
import { useLastGroupQuery } from '../FormPage/LoginPage/loginPage.gen.ts';
import { ExamsFromGroupDocument, useExamsFromGroupLazyQuery } from '../Game/game.gen.ts';
import './Backoffice.scss';
import { useExamRemoveMutation } from './backoffice.gen.ts';
import { BackofficeTest } from './components/BackofficeTest.tsx';
export const LIMIT = 10;

export const EMPTY_TEST = {
	definition: {
		answers: [],
		cards: [],
		initialBiddingSequence: [],
		__typename: 'ExamDefinitionView',
	},
	difficultyLevel: '',
	id: '',
	name: '',
	tags: [],
	updatedAt: '',
	vulnerability: ExamVulnerabilityView.NotSpecified,
	revisionId: '',
	__typename: 'ExamView',
};

const Backoffice = () => {
	const backofficeTests = useRef<null | HTMLDivElement>(null);
	const { data: chosenGroup } = useLastGroupQuery();
	const groupCode = chosenGroup?.me?.recentlyUsedGroupId || GENERAL_GROUP_NAME;
	const [editedTestId, setEditedTestId] = useState<string | null>(null);
	const [examRemoveMutation] = useExamRemoveMutation({ refetchQueries: [ExamsFromGroupDocument] });

	const [fetchGroups, { data, loading, refetch }] = useExamsFromGroupLazyQuery();

	const [offset, setOffset] = useState(0);
	const [emptyTest, setEmptyTest] = useState(false);
	const [page, setPage] = useState(1);
	const [tag, setTag] = useState<string>('');
	const fetchData = () => {
		fetchGroups({
			variables: {
				code: groupCode || GENERAL_GROUP_NAME,
				input: {
					pagination: {
						limit: LIMIT,
						offset: offset || 0,
					},
					filters: {
						difficultyLevel: [],
						tags: tag ? [tag] : [],
						ignoreExamsConnectedToLessons: true,
					},
				},
			},
		});
	};

	useEffect(() => {
		fetchData();
	}, [groupCode, offset, tag]);

	const total = data?.group?.exams?.total || 0;

	const count = Math.ceil(total / LIMIT);

	const onSave = () => {
		toast.success('Test zaktualizowany pomyślnie');
		tests?.length === 10 && emptyTest && handlePageChange(null, page + 1);
		setEmptyTest(false);
		refetch();
	};
	const handlePageChange = (_e: unknown, value: number) => {
		setPage(value);
		setOffset(value ? (value - 1) * LIMIT : 0);
		setEmptyTest(false);
	};

	const handleRemoveClick = async (id: string) => {
		const response = await examRemoveMutation({
			variables: {
				input: {
					examId: id,
					groupCode: groupCode,
				},
			},
		});
		if (response.data?.examDelete?.reason === null) {
			toast.success('Test został usunięty');
			fetchData();
			tests?.length === 1 && handlePageChange(null, page - 1);
		} else {
			toast.error('Nie udało się usunąć testu');
		}
	};

	const tests = data?.group?.exams?.items || [];

	const addEmptyTest = () => {
		handlePageChange(null, count);
		setEmptyTest(true);
		!isEmpty(tests) && scrollToBottom();
	};
	// useValidateToken();

	const scrollToBottom = () => {
		backofficeTests.current?.scrollIntoView({ behavior: 'smooth' });
	};

	const handleChange = (event: SelectChangeEvent<string>) => {
		const {
			target: { value },
		} = event;
		setTag(value);
	};
	return (
		<div className="backoffice">
			{loading && <Loading blur />}
			<Header />
			<div className="wrapper">
				<div className="backoffice__tests">
					{!emptyTest && isEmpty(tests) && !loading && !tag && (
						<div>
							<h2 className="backoffice__noTests">Dodaj swój pierwszy test!</h2>
							<img
								className="backoffice__help"
								src={window.location.origin + '/images/backoffice-help.png'}
								alt="Pomoc"
							/>
						</div>
					)}
					{!emptyTest && !loading && (
						<Button
							customClass="backoffice__add"
							variant="ghost-gold"
							text={`${isEmpty(tests) && !tag ? 'Rozumiem' : 'Dodaj'}`}
							onClick={addEmptyTest}
						/>
					)}
					{
						<div className="backoffice__pagination">
							{!isEmpty(tests) && !!page && (
								<>
									<Pagination
										siblingCount={3}
										boundaryCount={2}
										count={count}
										onChange={handlePageChange}
										page={page}
									/>
								</>
							)}
						</div>
					}
					<FormControl style={{ minWidth: 220, marginBottom: 20 }}>
						<InputLabel id="tag-label">Tag*</InputLabel>
						<Select labelId="tag-label" id="tag" value={tag} label="Tag*" onChange={handleChange}>
							{data?.group?.possibleExamTags.map((tag) => (
								<MenuItem key={tag} value={tag}>
									{tag}
								</MenuItem>
							))}
							<MenuItem key={'null'} value={undefined}>
								Wszystkie
							</MenuItem>
						</Select>
					</FormControl>
					{isEmpty(tests) && tag && <p>Brak testów w tej kategorii</p>}
					{tests.map((test) => (
						<BackofficeTest
							key={test.id}
							isForLesson={false}
							index={tests.indexOf(test) + 1 + offset}
							test={test}
							onSave={onSave}
							editMode={editedTestId === test.id}
							handleLeaveEditClick={() => {
								setEditedTestId(null);
							}}
							handleEditClick={() => {
								setEditedTestId(test.id);
							}}
							handleRemoveClick={handleRemoveClick}
						/>
					))}
					{emptyTest && (
						<BackofficeTest
							index={tests.length + 1 + offset}
							key="empty"
							isForLesson={false}
							test={{ ...EMPTY_TEST }}
							onSave={onSave}
							editMode={true}
							handleLeaveEditClick={() => {
								setEmptyTest(false);
								setEditedTestId(null);
							}}
							handleEditClick={() => {}}
							handleRemoveClick={handleRemoveClick}
						/>
					)}
				</div>
				<div ref={backofficeTests} />
				{!emptyTest && !isEmpty(tests) && !loading && (
					<Button customClass="backoffice__add" variant="ghost-gold" text="Dodaj" onClick={addEmptyTest} />
				)}
			</div>
			{
				<div className="backoffice__pagination">
					{!isEmpty(tests) && !!page && (
						<Pagination
							siblingCount={3}
							boundaryCount={2}
							count={count}
							onChange={handlePageChange}
							page={page}
						/>
					)}
				</div>
			}
			<Footer />
		</div>
	);
};

export default Backoffice;
